import { useContext, useEffect, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { RmaApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import DataFormSelect from "components/Core/DataFormSelect";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";

import { mergeCropProfileCommodities } from "./cropProfileCommodities";

// ---

export const COMMODITY_NAME_WHEAT = "Wheat";

export const CropProfileEditDialog = ({ cropProfile, onClose, onChange, readOnlyFields = [], initialValues = {} }) => {
  const { globalState } = useContext(AppContext);
  const { actions: formStatusActions } = useFormStatus();

  const [state, setState] = useState({});
  const { enqueueSnackbar } = useSnackbar();

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  // ---

  useEffect(() => {
    // Load default state:
    if (cropProfile) {
      const _cropProfile = {
        marketing_goal_units: 0,
        acreage_irrigated: null,
        yield_irrigated: null,
        acreage_non_irrigated: null,
        yield_non_irrigated: null,
        exchange_commodity_code: null,
        ...cropProfile,
        year: initialValues.year || cropProfile.year,
        commodity_name: initialValues.commodity_name || cropProfile.commodity_name,
        name: initialValues.commodity_name || cropProfile.name,
      };
      setState(_cropProfile);
    } else {
      setState({});
    }
  }, [cropProfile]);

  const onSubmit = () => {
    if (!validateForm()) {
      return;
    }
    const _state = {
      ...state,
      basis: state.basis !== "" && state.basis !== null ? state.basis : null,
    };
    onChange?.(_state);
    onClose?.();
  };

  // ---

  const handleChangeStateSelect = (event, value) => {
    if (value === null && event.target.value !== null) {
      value = event.target.value;
    }

    formStatusActions.setIsFormDirty(true, false);

    if (state[event.target.name] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      const _newState = { ...state };
      _newState[event.target.name] = value;

      if (event.target.name === "commodity_name") {
        if (value !== COMMODITY_NAME_WHEAT) {
          _newState["exchange_commodity_code"] = "";
        }

        _newState["name"] = value;
      }

      setState(_newState);
    }
  };

  const handleChangeStateSelectDropdown = (event) => {
    const _newState = { ...state };
    _newState[event.target.name] = event.target.value;
    setState(_newState);
  };

  const handleChangeState = async (event, value) => {
    if (value == null && event.target.value != null) {
      value = event.target.value;
    }

    if (state[event.target.name] != value) {
      formStatusActions.setIsFormDirty(true, false);

      const _newState = { ...state };
      _newState[event.target.name] = value;
      setState(_newState);
    }
  };

  const validateForm = () => {
    const validationRules = [
      { field: "year", message: "Year is required." },
      { field: "commodity_name", message: "Commodity is required." },
      { field: "marketing_goal_units", message: "Marketing Goal is required." },
    ];

    for (const rule of validationRules) {
      const value = state?.[rule.field];
      const isEmpty = value == null || value === "";
      if (isEmpty && (rule.condition === undefined || rule.condition)) {
        enqueueSnackbar(rule.message, { variant: "error" });
        return false;
      }
    }
    return true;
  };

  // Wheat exchange labels: KC=HRW (KE), MGE=Spring (MW), CBT=SRW (ZW)
  const exchanges = [
    {
      exchange_commodity_code: "KE",
      type_name: "Winter",
      type_extended_name: "HRW",
      exchange_name: "KC",
    },
    {
      exchange_commodity_code: "MW",
      type_name: "Spring",
      type_extended_name: "Spring",
      exchange_name: "MGE",
    },
    {
      exchange_commodity_code: "ZW",
      type_name: "Winter",
      type_extended_name: "SRW",
      exchange_name: "CBT",
    },
  ];

  // ---

  const formMaxWidthShort = "800px";

  return (
    <>
      <DialogTitle>Crop Profile</DialogTitle>

      <DialogContent>
        <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth={true}>
                <InputLabel id="yearLabel">Year</InputLabel>
                <Select
                  labelId="yearLabel"
                  value={state?.year || ""}
                  name={"year"}
                  onChange={handleChangeStateSelectDropdown}
                  label={"Year"}
                  disabled={readOnlyFields.includes("year")}
                  data-testid="crop-profile-year-select"
                >
                  {getBudgetYears().map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth={true}>
                <DataFormSelect
                  value={state?.commodity_name || ""}
                  name="commodity_name"
                  isRequired={true}
                  onChange={handleChangeStateSelect}
                  objectNameLabel="Commodity"
                  itemGetKey={(item) => item.commodity_name}
                  itemGetLabel={(item) => item.commodity_name}
                  doFetch={true}
                  isLoading={false}
                  multiple={false}
                  limitTags={isSmall ? 2 : 3}
                  disabled={readOnlyFields.includes("commodity_name")}
                  customDataLoader={async () =>
                    mergeCropProfileCommodities(await RmaApiHelper.searchCommodity({}, globalState))
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth={true}>
                <StatefulControlledTextField
                  label="Label"
                  name="name"
                  defaultValue={state?.name || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth={true}>
                <StatefulControlledTextField
                  label="Marketing Goal (# Units)"
                  name="marketing_goal_units"
                  isRequired={false}
                  defaultValue={state?.marketing_goal_units || 0}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth={true}>
                <StatefulControlledTextField
                  label="Basis"
                  name="basis"
                  isRequired={false}
                  defaultValue={state?.basis ?? ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                  allowNull={true}
                />
              </FormControl>
            </Grid>
          </Grid>

          {state?.commodity_name == COMMODITY_NAME_WHEAT && <Divider sx={{ my: 2 }} />}

          {state?.commodity_name == COMMODITY_NAME_WHEAT && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={exchanges.find((ex) => ex.exchange_commodity_code === state?.exchange_commodity_code) || null}
                  onChange={(event, newValue) => {
                    formStatusActions.setIsFormDirty(true, false);
                    const _newState = { ...state };
                    _newState.exchange_commodity_code = newValue?.exchange_commodity_code || null;
                    setState(_newState);
                  }}
                  autoSelect
                  autoHighlight
                  options={exchanges || []}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.exchange_name} - ${option.type_extended_name} (${option.exchange_commodity_code})`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.exchange_commodity_code === value?.exchange_commodity_code
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={"Choose Exchange"}
                      name={"exchange"}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={onSubmit}>
            OK
          </Button>
          <Button variant="contained" color="secondary" autoFocus onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};
